import Cookies from 'js-cookie';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import logo from '../assets/img/logo.png';
import AvatarIcon from '../assets/svg/AvatarIcon';
import LogoutIcon from '../assets/svg/LogoutIcon';
import { ROUTE_MAP, USER_ROLE } from '../constants';
import { useAuthContext } from '../hooks';

const ROUTES_BY_USER_ROLE_MAP = {
  [USER_ROLE.USER]: [
    {
      route: ROUTE_MAP.DASHBOARD,
      title: 'Dashboard',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          className="h-5 w-5"
          viewBox="0 0 24 24"
        >
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
      ),
    },
    {
      route: ROUTE_MAP.CALL_CENTER,
      title: 'Call Center',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
          className="h-5 w-5"
        >
          <path d="M10 0a10 10 0 1 0 10 10A10.01 10.01 0 0 0 10 0Zm8.426 9.23h-2.322a6.163 6.163 0 0 0-5.335-5.334V1.574a8.474 8.474 0 0 1 7.657 7.657Zm-9.195 0H5.45a4.624 4.624 0 0 1 3.78-3.78v3.78Zm0 1.54v3.78a4.624 4.624 0 0 1-3.781-3.78h3.78Zm1.538 0h3.781a4.624 4.624 0 0 1-3.78 3.78v-3.78Zm0-1.54V5.45a4.624 4.624 0 0 1 3.781 3.78h-3.78ZM9.231 1.575v2.322a6.164 6.164 0 0 0-5.335 5.335H1.574a8.474 8.474 0 0 1 7.657-7.657ZM1.574 10.77h2.322a6.163 6.163 0 0 0 5.335 5.335v2.322a8.475 8.475 0 0 1-7.657-7.657Zm9.195 7.657v-2.322a6.163 6.163 0 0 0 5.335-5.335h2.322a8.474 8.474 0 0 1-7.657 7.657Z" />
        </svg>
      ),
    },
    {
      route: ROUTE_MAP.BOOKINGS_REPORT,
      title: 'Bookings Report',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="h-5 w-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
          />
        </svg>
      ),
    },
  ],
  [USER_ROLE.MANAGER]: [
    {
      route: ROUTE_MAP.DASHBOARD,
      title: 'Dashboard',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          className="h-5 w-5"
          viewBox="0 0 24 24"
        >
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
      ),
    },
    {
      route: ROUTE_MAP.BOOKINGS_REPORT,
      title: 'Bookings Report',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="h-5 w-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
          />
        </svg>
      ),
    },
  ],
};

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { userRole, setUserRole } = useAuthContext();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const logout = async () => {
    try {
      const response = await fetch('/logout');
      if (response.status !== 200) throw new Error();
      setUserRole(null);
      navigate(ROUTE_MAP.LOGIN, { replace: true });
    } catch {
      toast.error("Can't logout. Please try again!", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="h-full text-primary">
      <div className="app flex h-full flex-col lg:flex-row">
        <header className="w-full bg-primary bg-cover bg-center bg-no-repeat py-4 lg:h-[100vh] lg:w-72 lg:py-8">
          <div className="flex h-full justify-between gap-10 px-5 lg:flex-col">
            <Link to={ROUTE_MAP.DASHBOARD}>
              <img src={logo} alt="ProspectIQ Logo" className="h-9 w-[248px]" />
            </Link>
            <ul className="hidden flex-1 space-y-1.5 lg:block">
              <SidebarNavigation
                routes={ROUTES_BY_USER_ROLE_MAP[userRole]}
                navigate={navigate}
              />
            </ul>
            <div className="hidden lg:block">
              <ButtonSignOut logout={logout} />
            </div>
            <button
              type="button"
              onClick={() => {
                setIsSidebarVisible(prev => !prev);
              }}
              className="text-white/70 transition hover:text-white lg:hidden cursor-pointer"
              data-hs-overlay="#mobile-sidebar"
              aria-controls="mobile-sidebar"
              aria-label="Toggle navigation"
            >
              <span className="sr-only">Toggle Navigation</span>
              <svg
                className="h-9 w-9 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
            </button>
          </div>
          <div
            id="mobile-sidebar"
            className={`hs-overlay fixed bottom-0 start-0 top-0 z-[60] w-72 transform overflow-y-auto bg-primary transition-all duration-300 ${isSidebarVisible ? 'translate-x-0' : '-translate-x-full'} [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2`}
          >
            <div className="flex h-full flex-col justify-between gap-5 p-5">
              <Link to={ROUTE_MAP.DASHBOARD}>
                <img src={logo} alt="ProspectIQ Logo" className="h-9" />
              </Link>
              <nav
                className="hs-accordion-group mt-3 flex w-full flex-1 flex-col flex-wrap"
                data-hs-accordion-always-open
              >
                <ul className="flex-1 space-y-1.5">
                  <SidebarNavigation
                    routes={ROUTES_BY_USER_ROLE_MAP[userRole]}
                    navigate={navigate}
                  />
                </ul>
              </nav>
              <div className="block lg:hidden">
                <ButtonSignOut logout={logout} />
              </div>
            </div>
          </div>
        </header>
        <main className="h-full flex-1 overflow-y-auto overflow-x-hidden bg-primary bg-hero bg-cover bg-center bg-no-repeat">
          <div className="h-[100vh] flex flex-col overflow-y-auto overflow-x-hidden rounded-t-2xl bg-neutral-100 lg:rounded-s-[3rem] lg:rounded-tr-none p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarNavigation = ({ routes, navigate }) => {
  return routes.map(({ route, title, icon }) => (
    <li key={route} className="cursor-pointer">
      <span
        className="group flex items-center gap-2 rounded-lg px-2.5 py-2 font-medium text-white transition hover:bg-secondary hover:text-white"
        onClick={() => {
          navigate(route);
        }}
      >
        <span className="text-secondary transition group-hover:text-white">
          {icon}
        </span>
        <span>{title}</span>
      </span>
    </li>
  ));
};

const ButtonSignOut = ({ logout }) => {
  const fullName = Cookies.get('fullName');

  return (
    <button
      className="group w-full flex items-center justify-between gap-2.5 flex-nowrap py-2 px-2.5 rounded-lg hover:bg-secondary"
      onClick={logout}
    >
      <div className="flex items-center flex-nowrap gap-2 truncate">
        <AvatarIcon className="w-6 h-6" />
        <span className="text-white font-medium text-[16px]/[24px] truncate">
          {fullName}
        </span>
      </div>
      <LogoutIcon className="w-5 h-5 text-brand-300 group-hover:text-white" />
    </button>
  );
};
